import React, { useState } from 'react';
import { Card, Typography, Table, Tag, message, Button, Row, Col, Space, Modal, Form, Input, InputNumber, Switch, Select, Popconfirm, Tabs } from 'antd';
import { PercentageOutlined, ArrowLeftOutlined, PlusOutlined, EditOutlined, DeleteOutlined, MenuOutlined, TeamOutlined, AppstoreOutlined } from '@ant-design/icons';
import AdminStaff from './AdminStaff';
import BusinessTypes from './BusinessTypes';

const { Title } = Typography;
const { Option } = Select;

const KirazeeManagement = () => {
  const [view, setView] = useState('menu'); // 'menu' | 'gst-slabs' | 'nav-items' | 'staff-management' | 'business-types'
  const [slabs, setSlabs] = useState([]);
  const [navItems, setNavItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingSlab, setEditingSlab] = useState(null);
  const [isNavItemModalVisible, setIsNavItemModalVisible] = useState(false);
  const [editingNavItem, setEditingNavItem] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();
  const [navItemForm] = Form.useForm();

  const fetchNavItems = async () => {
    setLoading(true);
    setView('nav-items');
    try {
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/kirazee/api/v1/admin/nav-items/`);
      const data = await response.json();
      if (data.success || data.data) {
        setNavItems(data.data || []);
      } else {
        message.error(data.message || 'Failed to fetch nav items');
      }
    } catch (error) {
      console.error('Error fetching nav items:', error);
      message.error('An error occurred while fetching nav items');
    } finally {
      setLoading(false);
    }
  };

  const fetchGstSlabs = async () => {
    setLoading(true);
    setView('gst-slabs');
    try {
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/kirazee/api/v1/admin/tax-slabs/`);
      const data = await response.json();
      if (data.success) {
        setSlabs(data.data);
      } else {
        message.error(data.message || 'Failed to fetch tax slabs');
      }
    } catch (error) {
      console.error('Error fetching tax slabs:', error);
      message.error('An error occurred while fetching tax slabs');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingSlab(null);
    form.resetFields();
    form.setFieldsValue({
      is_active: true,
      gst_percentage: 0,
      cgst_percentage: 0,
      sgst_percentage: 0,
      igst_percentage: 0,
      ugst_percentage: 0,
      tax_type: 'GST'
    });
    setIsModalVisible(true);
  };

  const handleEdit = (record) => {
    setEditingSlab(record);
    form.setFieldsValue(record);
    setIsModalVisible(true);
  };

  const handleDelete = async (id) => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/kirazee/api/v1/admin/tax-master/${id}/`, {
        method: 'DELETE',
      });
      if (response.ok) {
        message.success('GST Slab deleted successfully');
        fetchGstSlabs();
      } else {
        const data = await response.json();
        message.error(data.message || 'Failed to delete GST slab');
      }
    } catch (error) {
      console.error('Error deleting GST slab:', error);
      message.error('An error occurred while deleting');
    }
  };

  const handleModalSubmit = async (values) => {
    setSaving(true);
    try {
      const url = editingSlab
        ? `${import.meta.env.VITE_API_BASE_URL}/kirazee/api/v1/admin/tax-master/${editingSlab.id}/`
        : `${import.meta.env.VITE_API_BASE_URL}/kirazee/api/v1/admin/tax-master/`;
      const method = editingSlab ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(values),
      });

      const data = await response.json();
      if (response.ok || data.success) {
        message.success(`GST Slab ${editingSlab ? 'updated' : 'created'} successfully`);
        setIsModalVisible(false);
        fetchGstSlabs();
      } else {
        message.error(data.message || `Failed to ${editingSlab ? 'update' : 'create'} GST slab`);
      }
    } catch (error) {
      console.error(`Error ${editingSlab ? 'updating' : 'creating'} GST slab:`, error);
      message.error('An error occurred while saving');
    } finally {
      setSaving(false);
    }
  };

  const handleAddNavItem = () => {
    setEditingNavItem(null);
    navItemForm.resetFields();
    navItemForm.setFieldsValue({
      status: true,
      is_visible: true,
      order_index: 0,
      source: ''
    });
    setIsNavItemModalVisible(true);
  };

  const handleEditNavItem = (record) => {
    setEditingNavItem(record);
    navItemForm.setFieldsValue(record);
    setIsNavItemModalVisible(true);
  };

  const handleDeleteNavItem = async (id) => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/kirazee/api/v1/admin/nav-items/${id}/`, {
        method: 'DELETE',
      });
      if (response.ok) {
        message.success('Nav Item deleted successfully');
        fetchNavItems();
      } else {
        const data = await response.json();
        message.error(data.message || 'Failed to delete Nav Item');
      }
    } catch (error) {
      console.error('Error deleting Nav Item:', error);
      message.error('An error occurred while deleting');
    }
  };

  const handleNavItemModalSubmit = async (values) => {
    setSaving(true);
    try {
      const url = editingNavItem
        ? `${import.meta.env.VITE_API_BASE_URL}/kirazee/api/v1/admin/nav-items/${editingNavItem.id}/`
        : `${import.meta.env.VITE_API_BASE_URL}/kirazee/api/v1/admin/nav-items/`;
      const method = editingNavItem ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(values),
      });

      const data = await response.json();
      if (response.ok || data.success) {
        message.success(`Nav Item ${editingNavItem ? 'updated' : 'created'} successfully`);
        setIsNavItemModalVisible(false);
        fetchNavItems();
      } else {
        message.error(data.message || `Failed to ${editingNavItem ? 'update' : 'create'} Nav Item`);
      }
    } catch (error) {
      console.error(`Error ${editingNavItem ? 'updating' : 'creating'} Nav Item:`, error);
      message.error('An error occurred while saving');
    } finally {
      setSaving(false);
    }
  };



  const navItemsColumns = [
    { title: 'Nav Name', dataIndex: 'nav_name', key: 'nav_name' },
    { title: 'Sub Nav', dataIndex: 'sub_nav', key: 'sub_nav' },
    { title: 'Route Path', dataIndex: 'route_path', key: 'route_path' },
    { title: 'Order', dataIndex: 'order_index', key: 'order_index' },
    { title: 'Source', dataIndex: 'source', key: 'source' },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={status ? 'green' : 'red'}>{status ? 'ACTIVE' : 'INACTIVE'}</Tag>
      ),
    },
    {
      title: 'Visible',
      dataIndex: 'is_visible',
      key: 'is_visible',
      render: (isVisible) => (
        <Tag color={isVisible ? 'blue' : 'default'}>{isVisible ? 'YES' : 'NO'}</Tag>
      ),
    },
    {
      title: 'Action',
      key: 'action',
      render: (_, record) => (
        <Space>
          <Button type="primary" icon={<EditOutlined />} size="small" onClick={() => handleEditNavItem(record)}>
            Edit
          </Button>
          <Popconfirm
            title="Are you sure you want to delete this Nav Item?"
            onConfirm={() => handleDeleteNavItem(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button danger icon={<DeleteOutlined />} size="small">
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const columns = [
    { title: 'Tax Name', dataIndex: 'tax_name', key: 'tax_name' },
    { title: 'GST %', dataIndex: 'gst_percentage', key: 'gst_percentage', render: (val) => `${val}%` },
    { title: 'CGST %', dataIndex: 'cgst_percentage', key: 'cgst_percentage', render: (val) => `${val}%` },
    { title: 'SGST %', dataIndex: 'sgst_percentage', key: 'sgst_percentage', render: (val) => `${val}%` },
    { title: 'IGST %', dataIndex: 'igst_percentage', key: 'igst_percentage', render: (val) => `${val}%` },
    { title: 'Type', dataIndex: 'tax_type', key: 'tax_type', render: (val) => val?.toUpperCase() },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (isActive) => (
        <Tag color={isActive ? 'green' : 'red'}>{isActive ? 'ACTIVE' : 'INACTIVE'}</Tag>
      ),
    },
    {
      title: 'Action',
      key: 'action',
      render: (_, record) => (
        <Space>
          <Button type="primary" icon={<EditOutlined />} size="small" onClick={() => handleEdit(record)}>
            Edit
          </Button>
          <Popconfirm
            title="Are you sure you want to delete this GST slab?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button danger icon={<DeleteOutlined />} size="small">
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px', width: '100%' }}>
      {view === 'menu' ? (
        <>
          <Title level={2} style={{ color: '#2c3e50', marginBottom: '24px' }}>
            Kirazee Management
          </Title>
          <Row gutter={16}>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Card
                hoverable
                onClick={fetchGstSlabs}
                style={{ textAlign: 'center', borderRadius: '12px', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}
              >
                <PercentageOutlined style={{ fontSize: '48px', color: '#F55D00', marginBottom: '16px' }} />
                <Title level={4}>GST Slabs</Title>
              </Card>
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Card
                hoverable
                onClick={fetchNavItems}
                style={{ textAlign: 'center', borderRadius: '12px', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}
              >
                <MenuOutlined style={{ fontSize: '48px', color: '#2c3e50', marginBottom: '16px' }} />
                <Title level={4}>Nav Management</Title>
              </Card>
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Card
                hoverable
                onClick={() => setView('staff-management')}
                style={{ textAlign: 'center', borderRadius: '12px', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}
              >
                <TeamOutlined style={{ fontSize: '48px', color: '#1890ff', marginBottom: '16px' }} />
                <Title level={4}>Staff Management</Title>
              </Card>
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Card
                hoverable
                onClick={() => setView('business-types')}
                style={{ textAlign: 'center', borderRadius: '12px', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}
              >
                <AppstoreOutlined style={{ fontSize: '48px', color: '#52c41a', marginBottom: '16px' }} />
                <Title level={4}>Business Types</Title>
              </Card>
            </Col>
          </Row>
        </>
      ) : view === 'nav-items' ? (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '16px' }}>
            <Title level={2} style={{ color: '#2c3e50', margin: 0 }}>
              Nav Management
            </Title>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <Button type="primary" icon={<PlusOutlined />} onClick={handleAddNavItem}>
                Add Nav Item
              </Button>
              <Button icon={<ArrowLeftOutlined />} onClick={() => setView('menu')}>
                Back
              </Button>
            </div>
          </div>
          <Card bordered={false} style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.05)', borderRadius: '12px' }}>
            <Tabs
              defaultActiveKey="rbo"
              items={[
                {
                  key: 'rbo',
                  label: 'RBO',
                  children: (
                    <Table
                      columns={navItemsColumns}
                      dataSource={navItems.filter(item => item.source === 'rbo')}
                      rowKey="id"
                      loading={loading}
                      pagination={{ pageSize: 10 }}
                      scroll={{ x: 'max-content' }}
                    />
                  )
                },
                {
                  key: 'admin',
                  label: 'Admin',
                  children: (
                    <Table
                      columns={navItemsColumns}
                      dataSource={navItems.filter(item => item.source === 'admin')}
                      rowKey="id"
                      loading={loading}
                      pagination={{ pageSize: 10 }}
                      scroll={{ x: 'max-content' }}
                    />
                  )
                },
                {
                  key: 'delivery_partner',
                  label: 'Delivery Partner',
                  children: (
                    <Table
                      columns={navItemsColumns}
                      dataSource={navItems.filter(item => item.source === 'delivery_partner')}
                      rowKey="id"
                      loading={loading}
                      pagination={{ pageSize: 10 }}
                      scroll={{ x: 'max-content' }}
                    />
                  )
                }
              ]}
            />
          </Card>
        </>
      ) : view === 'staff-management' ? (
        <AdminStaff setView={setView} />
      ) : view === 'business-types' ? (
        <BusinessTypes setView={setView} />
      ) : (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '16px' }}>
            <Title level={2} style={{ color: '#2c3e50', margin: 0 }}>
              GST Slabs
            </Title>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
                Add GST Slab
              </Button>
              <Button icon={<ArrowLeftOutlined />} onClick={() => setView('menu')}>
                Back
              </Button>
            </div>
          </div>
          <Card bordered={false} style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.05)', borderRadius: '12px' }}>
            <Table
              columns={columns}
              dataSource={slabs}
              rowKey="id"
              loading={loading}
              pagination={{ pageSize: 10 }}
              scroll={{ x: 'max-content' }}
            />
          </Card>
        </>
      )}

      <Modal
        title={editingSlab ? "Edit GST Slab" : "Add GST Slab"}
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        onOk={() => form.submit()}
        confirmLoading={saving}
      >
        <Form form={form} layout="vertical" onFinish={handleModalSubmit}>
          <Form.Item name="tax_name" label="Tax Name" rules={[{ required: true, message: 'Please enter tax name' }]}>
            <Input placeholder="e.g. GST 18%" />
          </Form.Item>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="gst_percentage" label="GST %" rules={[{ required: true }]}>
                <InputNumber style={{ width: '100%' }} min={0} step={0.1} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="cgst_percentage" label="CGST %" rules={[{ required: true }]}>
                <InputNumber style={{ width: '100%' }} min={0} step={0.1} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="sgst_percentage" label="SGST %" rules={[{ required: true }]}>
                <InputNumber style={{ width: '100%' }} min={0} step={0.1} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="igst_percentage" label="IGST %" rules={[{ required: true }]}>
                <InputNumber style={{ width: '100%' }} min={0} step={0.1} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="ugst_percentage" label="UGST %" rules={[{ required: true }]}>
                <InputNumber style={{ width: '100%' }} min={0} step={0.1} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="tax_type" label="Tax Type" rules={[{ required: true }]}>
                <Select>
                  <Option value="GST">GST</Option>
                  <Option value="exclusive">Exclusive</Option>
                  <Option value="inclusive">Inclusive</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="is_active" label="Active Status" valuePropName="checked">
            <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={editingNavItem ? "Edit Nav Item" : "Add Nav Item"}
        open={isNavItemModalVisible}
        onCancel={() => setIsNavItemModalVisible(false)}
        onOk={() => navItemForm.submit()}
        confirmLoading={saving}
      >
        <Form form={navItemForm} layout="vertical" onFinish={handleNavItemModalSubmit}>
          <Form.Item name="nav_name" label="Nav Name" rules={[{ required: true, message: 'Please enter nav name' }]}>
            <Input placeholder="e.g. Dashboard" />
          </Form.Item>
          <Form.Item name="sub_nav" label="Sub Nav (Optional)">
            <Input placeholder="e.g. Overview" />
          </Form.Item>
          <Form.Item name="route_path" label="Route Path (Optional)">
            <Input placeholder="e.g. /dashboard" />
          </Form.Item>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="order_index" label="Order Index">
                <InputNumber style={{ width: '100%' }} min={0} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="icon" label="Icon Name (Optional)">
                <Input placeholder="e.g. DashboardOutlined" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="source" label="Source" rules={[{ required: true, message: 'Please select a source' }]}>
                <Select placeholder="Select source" allowClear>
                  <Option value="rbo">rbo</Option>
                  <Option value="admin">admin</Option>
                  <Option value="delivery_partner">delivery_partner</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="status" label="Status" valuePropName="checked">
                <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="is_visible" label="Visibility" valuePropName="checked">
                <Switch checkedChildren="Visible" unCheckedChildren="Hidden" />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>


    </div>
  );
};

export default KirazeeManagement;
